import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { buildMonthlyPdfReport } from "@/lib/pdf-report";
import { Switch } from "@/components/ui/switch";
import { PageHeader } from "@/components/PageHeader";
import { PeriodPicker } from "@/components/PeriodPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";
import {
  listCategories,
  listExpenses,
  listOccurrences,
  listPayments,
  listVendors,
} from "@/lib/api";
import { arListClients, arListCategories, arListCollections, arListOccurrences } from "@/lib/ar-api";
import {
  EUR,
  fmtDate,
  monthBounds,
  num,
  quarterBounds,
  semesterBounds,
  yearBounds,
} from "@/lib/format";
import { AlertTriangle, ArrowRight, BookOpen, FileText, Wallet } from "lucide-react";
import { MonthlyReportButton } from "@/components/MonthlyReportButton";
import { RemainingExposureDrillDown } from "@/components/RemainingExposureDrillDown";
import { NuovaScadenzaChoiceDialog } from "@/components/NuovaScadenzaChoiceDialog";

/**
 * Cruscotto — executive financial control center.
 *
 * Single source of truth: a period selection ([from, to]) drives every
 * KPI and panel on the page. The Cassa/Competenza toggle changes only
 * how "Pagato" is computed; remaining-exposure math (Pianificato, Da pagare,
 * Scaduto) always keys off the occurrence due_date for clean residual reading.
 *
 *   Competenza: Pagato = Σ total_paid of in-window occurrences
 *   Cassa:      Pagato = Σ payments where paid_at falls in [from, to]
 *
 * Excluded from every calculation: occurrences with status in
 * (cancelled, skipped) — they're audit-only and must NOT inflate exposure.
 */

const PRESETS = [
  { value: "month", label: "Mese" },
  { value: "quarter", label: "Trimestre" },
  { value: "semester", label: "Semestre" },
  { value: "year", label: "Anno" },
  { value: "custom", label: "Personalizzato" },
];

const computeBounds = (preset, customFrom, customTo) => {
  const today = new Date();
  switch (preset) {
    case "quarter":
      return quarterBounds(today);
    case "semester":
      return semesterBounds(today);
    case "year":
      return yearBounds(today);
    case "custom":
      return [customFrom, customTo];
    case "month":
    default:
      return monthBounds(today);
  }
};

const labelForPreset = (preset, from, to) => {
  const today = new Date();
  switch (preset) {
    case "quarter": {
      const q = Math.floor(today.getMonth() / 3) + 1;
      return `Trimestre Q${q} · ${today.getFullYear()}`;
    }
    case "semester": {
      const h = today.getMonth() < 6 ? "H1" : "H2";
      return `Semestre ${h} · ${today.getFullYear()}`;
    }
    case "year":
      return `Anno ${today.getFullYear()}`;
    case "custom":
      return `${fmtDate(from)} → ${fmtDate(to)}`;
    case "month":
    default:
      return today
        .toLocaleDateString("it-IT", { month: "long", year: "numeric" })
        .replace(/^./, (c) => c.toUpperCase());
  }
};

export default function Dashboard() {
  const [basis, setBasis] = useState("accrual");
  const [preset, setPreset] = useState("month");
  const [customFrom, setCustomFrom] = useState(() => monthBounds(new Date())[0]);
  const [customTo, setCustomTo] = useState(() => monthBounds(new Date())[1]);

  const [from, to] = useMemo(
    () => computeBounds(preset, customFrom, customTo),
    [preset, customFrom, customTo],
  );
  const periodLabel = labelForPreset(preset, from, to);

  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [paymentsInWindow, setPaymentsInWindow] = useState([]);
  const [arRows, setArRows] = useState([]); // receivable occurrences in window
  const [arCollections, setArCollections] = useState([]); // collections in window
  const [arClients, setArClients] = useState([]); // for PDF naming
  const [arCategories, setArCategories] = useState([]); // for PDF naming
  const [loading, setLoading] = useState(true);

  const [drillOpen, setDrillOpen] = useState(false);
  const [drillCatId, setDrillCatId] = useState(null);
  const [drillOnlyOverdue, setDrillOnlyOverdue] = useState(false);
  const [newChoiceOpen, setNewChoiceOpen] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  // Toggle: include the Incassi page in the exported PDF. Default ON.
  const [includeArInPdf, setIncludeArInPdf] = useState(true);

  const load = useCallback(async () => {
    if (!from || !to || from > to) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [occ, cats, vens, exps, pays, arOcc, arCol, arCli, arCats] = await Promise.all([
        listOccurrences({ date_from: from, date_to: to, limit: 1000 }),
        listCategories(),
        listVendors(),
        listExpenses(),
        // Payments in window — needed only for Cassa basis but cheap; keep
        // it in the same batch so the toggle flips instantly.
        listPayments({ date_from: from, date_to: to, limit: 5000 }),
        // Receivables side — used by the compact Saldo netto card.
        arListOccurrences({ date_from: from, date_to: to, limit: 1000 }),
        arListCollections({ date_from: from, date_to: to }),
        arListClients(),
        arListCategories(),
      ]);
      setRows(occ || []);
      setCategories(cats || []);
      setVendors(vens || []);
      setExpenses(exps || []);
      setPaymentsInWindow(pays || []);
      setArRows(arOcc || []);
      setArCollections(arCol || []);
      setArClients(arCli || []);
      setArCategories(arCats || []);
    } catch {
      setRows([]);
      setPaymentsInWindow([]);
      setArRows([]);
      setArCollections([]);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    load();
  }, [load]);

  /* ---- Derived KPIs (excluding cancelled/skipped) ----------------- */
  const todayIso = new Date().toISOString().slice(0, 10);
  const contributing = useMemo(
    () =>
      rows.filter((r) => r.status !== "cancelled" && r.status !== "skipped"),
    [rows],
  );

  const planned = useMemo(
    () => contributing.reduce((s, r) => s + num(r.effective_amount), 0),
    [contributing],
  );

  const paidAccrual = useMemo(
    () => contributing.reduce((s, r) => s + num(r.total_paid), 0),
    [contributing],
  );
  const paidCash = useMemo(
    () => paymentsInWindow.reduce((s, p) => s + num(p.paid_amount), 0),
    [paymentsInWindow],
  );
  const paid = basis === "cash" ? paidCash : paidAccrual;
  const remaining = Math.max(0, planned - paid);

  const overdueAmount = useMemo(() => {
    let s = 0;
    for (const r of contributing) {
      if (r.status !== "planned" && r.status !== "partially_paid") continue;
      if (!r.due_date || r.due_date >= todayIso) continue;
      s += num(r.effective_amount) - num(r.total_paid);
    }
    return s;
  }, [contributing, todayIso]);

  const overdueCount = useMemo(
    () =>
      contributing.filter(
        (r) =>
          (r.status === "planned" || r.status === "partially_paid") &&
          r.due_date < todayIso,
      ).length,
    [contributing, todayIso],
  );

  const paidPct = planned > 0
    ? Math.min(100, Math.max(0, (paid / planned) * 100))
    : 0;
  const paidPctIT = paidPct.toLocaleString("it-IT", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  /* ---- Saldo netto del periodo --------------------------------------
   *  Previsto = somma incassi attesi nel periodo − pianificato spese
   *             (esclude annullati/insoluti lato AR; annullati/saltati lato AP)
   *  Realizzato = incassi effettivi (cassa) − pagamenti effettivi (cassa)
   *               nel periodo.
   * Compact bridge KPI before Phase 2 unifies both sides.
   */
  const arPlanned = useMemo(() => {
    let s = 0;
    for (const r of arRows) {
      if (r.status === "cancelled" || r.status === "defaulted") continue;
      s += num(r.effective_amount);
    }
    return s;
  }, [arRows]);
  const arCollected = useMemo(
    () => arCollections.reduce((s, c) => s + num(c.collected_amount), 0),
    [arCollections],
  );
  const netExpected = arPlanned - planned;
  const netRealised = arCollected - paidCash;

  /* ---- "Da pagare adesso" — upcoming + overdue unpaid -------------- */
  const upcomingUnpaid = useMemo(() => {
    const list = contributing.filter(
      (r) => r.status === "planned" || r.status === "partially_paid",
    );
    list.sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
    return list;
  }, [contributing]);

  /* ---- "Residuo per categoria" — top exposure breakdown ------------- */
  const catMap = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.id, c])),
    [categories],
  );
  const venMap = useMemo(
    () => Object.fromEntries(vendors.map((v) => [v.id, v])),
    [vendors],
  );
  const expMap = useMemo(
    () => Object.fromEntries(expenses.map((e) => [e.id, e])),
    [expenses],
  );

  const breakdown = useMemo(() => {
    const acc = new Map();
    for (const r of contributing) {
      const rem = num(r.effective_amount) - num(r.total_paid);
      if (rem <= 0.005) continue;
      const cid = r.effective_category_id || "__none__";
      const cur = acc.get(cid) || { remaining: 0, count: 0 };
      cur.remaining += rem;
      cur.count += 1;
      acc.set(cid, cur);
    }
    const arr = Array.from(acc.entries()).map(([id, agg]) => ({
      id,
      name: catMap[id]?.name || "Senza categoria",
      ...agg,
    }));
    arr.sort((a, b) => b.remaining - a.remaining);
    return arr;
  }, [contributing, catMap]);
  const breakdownMax = breakdown[0]?.remaining || 1;

  /* ---- Handlers ------------------------------------------------------ */
  const openDrillRemaining = (catId = null) => {
    setDrillCatId(catId);
    setDrillOnlyOverdue(false);
    setDrillOpen(true);
  };
  const openDrillOverdue = () => {
    setDrillCatId(null);
    setDrillOnlyOverdue(true);
    setDrillOpen(true);
  };

  const titleOf = (r) => r.effective_title || expMap[r.expense_id]?.title || "—";

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Gestionale Incassi & Pagamenti"
        subtitle="Controllo economico, incassi, pagamenti, flussi finanziari e monitoraggio della liquidità"
        actions={
          <ToggleGroup
            type="single"
            value={basis}
            onValueChange={(v) => v && setBasis(v)}
            data-testid="dashboard-basis-toggle"
          >
            <ToggleGroupItem value="cash" data-testid="dashboard-basis-cash">
              Cassa
            </ToggleGroupItem>
            <ToggleGroupItem value="accrual" data-testid="dashboard-basis-accrual">
              Competenza
            </ToggleGroupItem>
          </ToggleGroup>
        }
      />

      {/* Primary actions */}
      <div className="mb-3 flex flex-wrap gap-2" data-testid="dashboard-primary-actions">
        <Button
          data-testid="cta-new-expense"
          onClick={() => setNewChoiceOpen(true)}
        >
          + Nuova scadenza
        </Button>
        <Button asChild variant="outline" data-testid="cta-view-analytics">
          <Link to="/analytics">Apri analisi interna</Link>
        </Button>
        <Button
          asChild
          variant="outline"
          data-testid="cta-view-prima-nota"
          title="Vai alla Prima Nota filtrata sul periodo selezionato"
        >
          <Link to={`/prima-nota?date_from=${from}&date_to=${to}`}>
            <BookOpen size={14} strokeWidth={1.8} />
            Vai in Prima Nota
          </Link>
        </Button>
        <MonthlyReportButton
          defaultDateFrom={from}
          defaultDateTo={to}
          defaultBasis={basis}
          testId="monthly-report"
        />
        <Button
          variant="outline"
          data-testid="cta-export-pdf"
          disabled={exportingPdf}
          onClick={async () => {
            if (exportingPdf) return;
            setExportingPdf(true);
            try {
              const fn = await buildMonthlyPdfReport({
                from,
                to,
                periodLabel,
                basis,
                occurrences: rows,
                expenses,
                vendors,
                categories,
                paidCash,
                arOccurrences: includeArInPdf ? arRows : [],
                arCollections: includeArInPdf ? arCollections : [],
                arClients: includeArInPdf ? arClients : [],
                arCategories: includeArInPdf ? arCategories : [],
              });
              toast.success(`PDF generato: ${fn}`);
            } catch (e) {
              toast.error("Errore generazione PDF");
            } finally {
              setExportingPdf(false);
            }
          }}
        >
          <FileText size={14} strokeWidth={1.8} />
          {exportingPdf ? "Generazione…" : "Esporta PDF"}
        </Button>
        <label
          className="ml-1 flex cursor-pointer items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 text-xs text-stone-700"
          data-testid="pdf-include-ar-toggle"
        >
          <Switch
            checked={includeArInPdf}
            onCheckedChange={setIncludeArInPdf}
            data-testid="pdf-include-ar-switch"
            aria-label="Includi pagina incassi nel PDF"
          />
          <span>Includi Incassi nel PDF</span>
        </label>
      </div>

      {/* Period selector */}
      <section
        data-testid="dashboard-period-selector"
        className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-stone-200 bg-white px-4 py-2"
      >
        <div className="flex items-center gap-2">
          <Wallet size={14} strokeWidth={1.8} className="text-stone-700" />
          <span className="text-sm font-semibold uppercase tracking-wider text-stone-700">
            Periodo
          </span>
          <span
            data-testid="dashboard-period-label"
            className="text-xs text-stone-500"
          >
            {periodLabel}
          </span>
          <PeriodPicker
            value={{ from: customFrom, to: customTo }}
            onChange={(f, t) => {
              setPreset("custom");
              setCustomFrom(f);
              setCustomTo(t);
            }}
            testId="dash-period"
          />
        </div>
        <ToggleGroup
          type="single"
          value={preset}
          onValueChange={(v) => v && setPreset(v)}
          data-testid="dashboard-period-preset"
          className="flex flex-wrap"
        >
          {PRESETS.map((p) => (
            <ToggleGroupItem
              key={p.value}
              value={p.value}
              data-testid={`dashboard-period-preset-${p.value}`}
              className="text-xs"
            >
              {p.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        {preset === "custom" && (
          <div
            data-testid="dashboard-period-custom"
            className="flex items-end gap-2"
          >
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-stone-500">
                Dal
              </Label>
              <Input
                type="date"
                data-testid="dashboard-period-custom-from"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-stone-500">
                Al
              </Label>
              <Input
                type="date"
                data-testid="dashboard-period-custom-to"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="h-9"
              />
            </div>
          </div>
        )}
      </section>

      {/* KPI strip */}
      <section
        data-testid="dashboard-kpi-strip"
        className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
      >
        <KpiCard
          testId="kpi-planned"
          label="Pianificato"
          value={planned}
          tone="stone"
          hint={`${contributing.length} ${contributing.length === 1 ? "scadenza" : "scadenze"} nel periodo`}
        />
        <KpiCard
          testId="kpi-paid"
          label={basis === "cash" ? "Pagato (cassa)" : "Pagato (competenza)"}
          value={paid}
          tone="emerald"
          hint={`${paidPctIT}% del pianificato`}
          footerLink={{
            to: `/prima-nota?date_from=${from}&date_to=${to}&tipo=out`,
            label: "Vedi pagamenti in Prima Nota",
            testId: "kpi-paid-prima-nota",
          }}
        />
        <KpiCard
          testId="kpi-remaining"
          label="Da pagare"
          value={remaining}
          tone={remaining > 0 ? "amber" : "emerald"}
          onClick={() => openDrillRemaining()}
          ctaLabel="Dettaglio →"
        />
        <KpiCard
          testId="kpi-overdue"
          label="Scaduto"
          value={overdueAmount}
          tone={overdueAmount > 0 ? "rose" : "stone"}
          icon={overdueAmount > 0 ? AlertTriangle : null}
          hint={
            overdueAmount > 0
              ? `${overdueCount} ${overdueCount === 1 ? "scadenza" : "scadenze"} oltre la data`
              : "Nessuno scaduto"
          }
          onClick={overdueAmount > 0 ? openDrillOverdue : undefined}
          ctaLabel={overdueAmount > 0 ? "Dettaglio →" : null}
        />
        <KpiCard
          testId="kpi-net-expected"
          label="Saldo previsionale netto"
          value={netExpected}
          tone={netExpected >= 0 ? "emerald" : "rose"}
          showSign
          hint={`Previsto incassi ${EUR(arPlanned)} − Pianificato spese ${EUR(planned)}`}
        />
      </section>

      {/* Progress bar */}
      <section
        data-testid="dashboard-progress"
        className="mt-3 rounded-xl border border-stone-200 bg-white p-3"
      >
        <div className="mb-1.5 flex items-center justify-between text-[11px] uppercase tracking-wider text-stone-500">
          <span>Progresso pagamenti</span>
          <span data-testid="dashboard-progress-pct" className="font-mono tabular-nums">
            {paidPctIT}%
          </span>
        </div>
        <div
          className="h-2.5 w-full overflow-hidden rounded-full bg-stone-100"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={paidPct}
          aria-label={`Pagato ${paidPctIT}% del pianificato`}
        >
          <div
            data-testid="dashboard-progress-fill"
            className="h-full rounded-full bg-emerald-500 transition-[width] duration-300"
            style={{ width: `${paidPct}%` }}
          />
        </div>
        <div className="mt-1.5 flex justify-between text-[11px] text-stone-500">
          <span>Coperto {EUR(paid)}</span>
          <span>Residuo {EUR(remaining)}</span>
        </div>
      </section>

      {/* Saldo netto del periodo — compact bridge KPI before Phase 2 */}
      <section
        data-testid="dashboard-net-balance"
        className="mt-3 rounded-xl border border-stone-200 bg-white p-3"
      >
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-stone-700">
            Saldo netto del periodo
          </h2>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-stone-500">
              Incassi previsti − spese pianificate
            </span>
            <Link
              to={`/prima-nota?date_from=${from}&date_to=${to}`}
              data-testid="net-balance-link-prima-nota"
              className="inline-flex items-center gap-1 rounded-md border border-primary/30 bg-primary/5 px-2 py-1 text-[11px] font-medium text-primary hover:bg-primary/10"
              title="Apri Prima Nota filtrata sul periodo (entrate e uscite reali)"
            >
              <BookOpen size={11} strokeWidth={2} />
              Vedi in Prima Nota
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <NetTile
            label="Previsto"
            value={netExpected}
            inflow={arPlanned}
            outflow={planned}
            inflowLabel="Previsto incassi"
            outflowLabel="Pianificato spese"
            testId="dashboard-net-expected"
          />
          <NetTile
            label="Realizzato"
            value={netRealised}
            inflow={arCollected}
            outflow={paidCash}
            inflowLabel="Incassato"
            outflowLabel="Pagato (cassa)"
            testId="dashboard-net-realised"
            primaNotaHref={`/prima-nota?date_from=${from}&date_to=${to}`}
          />
        </div>
      </section>

      {/* Two operational panels */}
      <section className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Da pagare adesso */}
        <div
          data-testid="dashboard-due-now"
          className="rounded-xl border border-stone-200 bg-white p-5"
        >
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-stone-700">
              Da pagare adesso
            </h2>
            <button
              type="button"
              data-testid="dashboard-due-now-open-all"
              onClick={() => openDrillRemaining()}
              className="inline-flex items-center gap-1 text-xs text-stone-600 hover:text-stone-900"
            >
              Apri tutto <ArrowRight size={12} />
            </button>
          </div>
          {upcomingUnpaid.length === 0 ? (
            <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs text-emerald-800">
              Tutte le scadenze del periodo sono saldate. ✓
            </p>
          ) : (
            <ul className="divide-y divide-stone-100">
              {upcomingUnpaid.slice(0, 8).map((r) => {
                const rem = num(r.effective_amount) - num(r.total_paid);
                const overdue = r.due_date < todayIso;
                return (
                  <li
                    key={r.id}
                    data-testid={`dashboard-due-row-${r.id}`}
                    className="py-2"
                  >
                    <button
                      type="button"
                      onClick={() => openDrillRemaining()}
                      className="flex w-full items-baseline justify-between gap-3 text-left transition-colors hover:bg-stone-50/60"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span
                            className={
                              "font-mono text-xs tabular-nums " +
                              (overdue
                                ? "font-semibold text-rose-700"
                                : "text-stone-700")
                            }
                          >
                            {fmtDate(r.due_date)}
                          </span>
                          {overdue && (
                            <span className="rounded-full bg-rose-600 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white">
                              Scaduta
                            </span>
                          )}
                          {r.status === "partially_paid" && (
                            <span className="rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-800">
                              Parziale
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 truncate text-sm font-medium text-stone-900">
                          {titleOf(r)}
                        </div>
                        <div className="truncate text-[11px] text-stone-500">
                          {venMap[r.effective_vendor_id]?.name || "Senza fornitore"}
                          {" · "}
                          {catMap[r.effective_category_id]?.name || "Senza categoria"}
                        </div>
                      </div>
                      <span
                        className={
                          "shrink-0 font-mono text-sm font-semibold tabular-nums " +
                          (overdue ? "text-rose-700" : "text-amber-900")
                        }
                      >
                        {EUR(rem)}
                      </span>
                    </button>
                  </li>
                );
              })}
              {upcomingUnpaid.length > 8 && (
                <li className="pt-2 text-center text-[11px] text-stone-500">
                  +{upcomingUnpaid.length - 8} altre scadenze · usa "Apri tutto"
                </li>
              )}
            </ul>
          )}
        </div>

        {/* Residuo per categoria */}
        <div
          data-testid="dashboard-by-category"
          className="rounded-xl border border-stone-200 bg-white p-5"
        >
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-stone-700">
              Residuo per categoria
            </h2>
            <Link
              to="/analytics"
              data-testid="dashboard-by-category-analytics-link"
              className="inline-flex items-center gap-1 text-xs text-stone-600 hover:text-stone-900"
            >
              Analisi completa <ArrowRight size={12} />
            </Link>
          </div>
          {breakdown.length === 0 ? (
            <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs text-emerald-800">
              Nessun residuo per categoria nel periodo.
            </p>
          ) : (
            <ul className="divide-y divide-stone-100">
              {breakdown.slice(0, 8).map((b) => {
                const pct = (b.remaining / breakdownMax) * 100;
                return (
                  <li
                    key={b.id}
                    data-testid={`dashboard-cat-${b.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => openDrillRemaining(b.id)}
                      className="flex w-full flex-col gap-1 px-1 py-2 text-left hover:bg-stone-50/60"
                    >
                      <div className="flex items-baseline justify-between gap-3 text-sm">
                        <span className="truncate font-medium text-stone-800">
                          {b.name}
                        </span>
                        <span className="flex items-center gap-2 font-mono tabular-nums">
                          <span className="font-semibold text-stone-900">
                            {EUR(b.remaining)}
                          </span>
                          <span className="text-[10px] text-stone-400">
                            ({b.count})
                          </span>
                        </span>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-stone-100/80">
                        <div
                          className="h-full rounded-full bg-amber-400"
                          style={{
                            width: `${Math.max(8, pct)}%`,
                            maxWidth: "100%",
                          }}
                        />
                      </div>
                    </button>
                  </li>
                );
              })}
              {breakdown.length > 8 && (
                <li className="pt-2 text-center text-[11px] text-stone-500">
                  +{breakdown.length - 8} altre categorie · clic su "Da pagare" per vederle
                </li>
              )}
            </ul>
          )}
        </div>
      </section>

      {loading && (
        <div className="mt-4 text-center text-[11px] text-stone-400">
          Calcolo in corso…
        </div>
      )}

      {/* Drill-down dialog (shared by Da pagare + Scaduto + category click) */}
      <RemainingExposureDrillDown
        open={drillOpen}
        onOpenChange={setDrillOpen}
        rows={rows}
        categories={categories}
        vendors={vendors}
        expenses={expenses}
        from={from}
        to={to}
        periodLabel={periodLabel}
        totalRemaining={drillOnlyOverdue ? overdueAmount : remaining}
        onChanged={load}
        initialCategoryId={drillCatId}
        onlyOverdue={drillOnlyOverdue}
        title={
          drillOnlyOverdue
            ? "Scaduto — dettaglio del periodo"
            : "Da pagare — dettaglio del periodo"
        }
      />

      <NuovaScadenzaChoiceDialog
        open={newChoiceOpen}
        onOpenChange={setNewChoiceOpen}
      />
    </div>
  );
}

function NetTile({ label, value, inflow, outflow, inflowLabel, outflowLabel, testId, primaNotaHref }) {
  const positive = value >= 0;
  const tone = positive ? "emerald" : "rose";
  const toneCls = {
    emerald: {
      wrap: "border-emerald-200 bg-emerald-50/40",
      label: "text-emerald-800",
      value: "text-emerald-900",
      hint: "text-emerald-700",
    },
    rose: {
      wrap: "border-rose-200 bg-rose-50/40",
      label: "text-rose-800",
      value: "text-rose-900",
      hint: "text-rose-700",
    },
  }[tone];
  // Sign-prefixed amount; EUR() already formats Italian style.
  const display = (positive ? "+" : "−") + EUR(Math.abs(value));
  return (
    <div
      data-testid={testId}
      className={"rounded-lg border px-4 py-3 " + toneCls.wrap}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className={"text-[11px] font-semibold uppercase tracking-wider " + toneCls.label}>
          {label}
        </span>
        <span
          data-testid={`${testId}-value`}
          className={"font-mono text-xl font-semibold tabular-nums " + toneCls.value}
        >
          {display}
        </span>
      </div>
      <div className={"mt-1 flex items-center justify-between gap-2 text-[11px] " + toneCls.hint}>
        <span>{inflowLabel} {EUR(inflow)} − {outflowLabel} {EUR(outflow)}</span>
        {primaNotaHref && (
          <Link
            to={primaNotaHref}
            data-testid={`${testId}-prima-nota`}
            className="inline-flex shrink-0 items-center gap-0.5 underline-offset-2 hover:underline"
            title="Apri Prima Nota — movimenti reali del periodo"
          >
            Prima Nota <ArrowRight size={10} strokeWidth={2} />
          </Link>
        )}
      </div>
    </div>
  );
}

function KpiCard({
  label,
  value,
  hint,
  tone = "stone",
  icon: Icon,
  onClick,
  ctaLabel,
  testId,
  footerLink,
  showSign,
}) {
  const tones = {
    stone: "border-stone-200 bg-white",
    emerald: "border-emerald-200 bg-emerald-50/40",
    amber: "border-amber-200 bg-amber-50/40",
    rose: "border-rose-200 bg-rose-50/40",
  };
  const labelTones = {
    stone: "text-stone-600",
    emerald: "text-emerald-800",
    amber: "text-amber-800",
    rose: "text-rose-800",
  };
  const valueTones = {
    stone: "text-stone-900",
    emerald: "text-emerald-900",
    amber: "text-amber-900",
    rose: "text-rose-900",
  };
  const hintTones = {
    stone: "text-stone-500",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
  };
  const ctaTones = {
    stone: "text-stone-500",
    emerald: "text-emerald-600",
    amber: "text-amber-700",
    rose: "text-rose-700",
  };

  const className =
    "flex min-w-0 flex-col items-stretch justify-between rounded-xl border px-4 py-3 " +
    (tones[tone] || tones.stone) +
    (onClick
      ? " cursor-pointer text-left transition-shadow hover:shadow-[0_2px_8px_rgba(15,17,21,0.08)] focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-700/40"
      : "");

  const content = (
    <>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          {Icon && <Icon size={13} strokeWidth={1.8} className={labelTones[tone]} />}
          <span
            className={
              "text-[11px] font-semibold uppercase tracking-wider " +
              (labelTones[tone] || labelTones.stone)
            }
          >
            {label}
          </span>
        </div>
        {ctaLabel && (
          <span
            aria-hidden
            className={"text-[10px] uppercase tracking-wider " + (ctaTones[tone] || ctaTones.stone)}
          >
            {ctaLabel}
          </span>
        )}
      </div>
      <div
        className={
          "mt-1 truncate font-mono text-2xl font-semibold tabular-nums " +
          (valueTones[tone] || valueTones.stone)
        }
        title={
          showSign
            ? `${value >= 0 ? "+" : "−"}${EUR(Math.abs(value))}`
            : EUR(value)
        }
      >
        {showSign
          ? `${value >= 0 ? "+" : "−"}${EUR(Math.abs(value))}`
          : EUR(value)}
      </div>
      {hint && (
        <div
          className={
            "mt-1 text-[11px] " + (hintTones[tone] || hintTones.stone)
          }
        >
          {hint}
        </div>
      )}
      {footerLink && (
        <Link
          to={footerLink.to}
          data-testid={footerLink.testId}
          onClick={(e) => e.stopPropagation()}
          className={
            "mt-2 inline-flex items-center gap-1 self-start text-[11px] underline-offset-2 hover:underline " +
            (hintTones[tone] || hintTones.stone)
          }
          title="Apri Prima Nota — movimenti reali del periodo"
        >
          {footerLink.label} <ArrowRight size={10} strokeWidth={2} />
        </Link>
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        className={className}
      >
        {content}
      </button>
    );
  }
  return (
    <div data-testid={testId} className={className}>
      {content}
    </div>
  );
}
